<script lang="ts" setup>
import { computed, ref } from "vue";
import { useMounted } from "@vueuse/core";
import { Swiper, SwiperSlide } from "swiper/vue";
import ProductNew from "@/components/home/ProductNew.vue";
import BaseLink from "@/components/buttons/BaseLink.vue";
import { vElementHover } from "@vueuse/components";
import { HomeService } from "~/services/HomeService";
import { Jsona } from "jsona";
import {
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  EffectFade,
} from "swiper/modules";

type ProductCard = Record<string, unknown> & {
  slug?: string;
};

const routeHydrated = useRouteHydrated();
const dataFormatter = new Jsona();
const products = useState<ProductCard[]>("home.newProducts", () => []);
const isHovered = ref(false);
const linkSeeMore = useState("home.moreNewProducts", () => "/");
const modules = [Navigation, Pagination, A11y, EffectFade, Scrollbar];
const mounted = useMounted();
const fallbackProducts = computed(() => products.value.slice(0, 10));

const onHover = (state: boolean) => {
  isHovered.value = state;
};

const search = async () => {
  const homeService = new HomeService();
  if (!routeHydrated.value) {
    try {
      const response = await homeService.getNewProducts();
      if (response) {
        products.value = dataFormatter.deserialize(response) as ProductCard[];
        if (response.links?.self) {
          linkSeeMore.value = response.links.self;
        }
      }
    } catch (err) {
      console.error(err);
    }
  }
};

if (import.meta.server) {
  await search();
} else {
  search(); // en cliente mostramos pantallas de carga
}
</script>

<template>
  <section>
    <div v-if="products.length > 0" class="mt-2 mt-lg-0 new-products">
      <p class="text-secondary title px-3 px-lg-0">
        Conoce los productos nuevos
      </p>
      <div class="px-2">
        <div class="row new-products-shell">
          <div class="col-12 col-lg-10 col-xl-11">
            <div
              v-if="!mounted"
              class="new-products-fallback d-flex align-items-stretch"
            >
              <div
                v-for="(product, index) in fallbackProducts"
                :key="index"
                class="new-products-fallback__item"
              >
                <product-new :product="product as Record<string, any>" />
              </div>
            </div>
            <swiper
              v-else
              :modules="modules"
              :navigation="false"
              :loop="true"
              :pagination="false"
              :scrollbar="{ draggable: true }"
              :breakpoints="{
                '320': {
                  slidesPerView: 3,
                  spaceBetween: 0,
                },
                '640': {
                  slidesPerView: 4,
                  spaceBetween: 0,
                },
                '768': {
                  slidesPerView: 4,
                  spaceBetween: 0,
                },
                '992': {
                  slidesPerView: 6,
                  spaceBetween: 0,
                },
                '1280': {
                  slidesPerView: 8,
                  spaceBetween: 0,
                },
                '1440': {
                  slidesPerView: 9,
                  spaceBetween: 0,
                },
                '1700': {
                  slidesPerView: 10,
                  spaceBetween: 0,
                },
              }"
              class="p-0"
            >
              <swiper-slide
                v-for="(product, index) in products"
                :key="index"
                class="my-auto align-self-stretch"
              >
                <product-new
                  :product="product as Record<string, any>"
                ></product-new>
              </swiper-slide>
            </swiper>
          </div>
          <div
            class="col-lg-2 col-xl-1 d-none d-lg-flex align-items-center justify-content-md-end justify-content-xl-start"
          >
            <base-link
              v-element-hover="onHover"
              class="d-flex"
              :class="{ 'shadow-inset text-danger': isHovered }"
              :to="linkSeeMore"
            >
              <span class="fs-6 text-nowrap text-secondary mt-1">
                Ver todos
              </span>
              <i class="bi bi-chevron-double-right fs-3 text-secondary" />
            </base-link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="sass" scoped>
.swiper
    width: 100%

.swiper-slide
    text-align: center
    font-size: 18px
    background: #fff
    display: flex
    justify-content: center
    align-items: center

.shadow-inset
    box-shadow: inset 0px 1px 5px var(--bs-gray-200)

.new-products-shell
    @media (min-width: 992px)
        box-shadow: var(--bs-box-shadow) !important
        border: 1px solid var(--bs-border-color) !important

.new-products-fallback
    overflow-x: auto
    scroll-snap-type: x proximity
    gap: 0
    -webkit-overflow-scrolling: touch

.new-products-fallback__item
    flex: 0 0 33.3333%
    min-width: 0
    scroll-snap-align: start

    @media (min-width: 640px)
        flex-basis: 25%

    @media (min-width: 992px)
        flex-basis: 16.6667%

    @media (min-width: 1280px)
        flex-basis: 12.5%
</style>
